'use client';

import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import type { SearchPageData } from '@/app/search/createSearchPageData';
import { PROMOTION_TYPES } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSearchPageData } from '../../search/searchPageDataProvider.tsx';
import { useOffersContext } from '../OffersProvider';

export function useFetchHotelPromotions() {
  const initialParams = useInitialParams();
  const searchPageData = useSearchPageData();
  const { offersData, isLoadingRooms } = useOffersContext();

  // Use promotions from rooms endpoint. Once rooms have loaded they are authoritative — the route
  // returns promotions: null when travel API has no promotion, and the search-page fallback must
  // not win over that (it may be stale, e.g. a deal that expired between search and detail).
  const fallbackData = adaptSearchPageHotelData(searchPageData, initialParams.internalHotelId);

  return {
    data: offersData ? offersData.promotions : fallbackData,
    error: undefined,
    isLoading: isLoadingRooms,
  };
}

export function useHotelPromotion() {
  const { localization } = usePageWrapperData();
  const { data: promotions } = useFetchHotelPromotions();

  const { promotionType } = promotions || {};
  const lastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;
  const hasFlashSalePromotion = promotionType === 'FLASH_SALE';

  return {
    showFlashSalesPromotion: hasFlashSalePromotion,
    showLmnDealsPromotion: lastMinuteDeals && hasFlashSalePromotion,
  };
}

// TOODO get checkin checkout promotion values from results
function adaptSearchPageHotelData(
  searchPageData: SearchPageData,
  internalHotelId: number
): PromotionSuccess | null | undefined {
  const hotelCardData = searchPageData?.productsData?.dealCards?.find(
    (card) => card.locales?.hotelId === internalHotelId
  );
  if (!hotelCardData || hotelCardData.promotionTheme !== PROMOTION_TYPES.FLASH_SALES) return undefined;

  return {
    // The hotel-detail pipeline uses the travel API spelling 'FLASH_SALE' (see promotionsAdapter),
    // not the search-side PROMOTION_TYPES.FLASH_SALES — useHotelPromotion matches on the former.
    promotionType: 'FLASH_SALE',
    bookDate: hotelCardData.product.promotions.flashSaleExpiration,
    isFlashSale: true,
    dynamicDeal: undefined,
  };
}
